// Admin report endpoints:
//  GET /api/admin/reports/dashboard?filter=
//  GET /api/admin/reports/auctions?filter=&status=&search=
//  GET /api/admin/reports/users?filter=&role=&search=
// filter: today | this_week | this_month | this_year | all

import { API_BASE_URL } from './apiConfig';

const ADMIN_REPORTS_BASE = `${API_BASE_URL}/admin/reports`;

export type ReportPeriod = 'today' | 'this_week' | 'this_month' | 'this_year' | 'all';

export interface AdminDashboardReport {
  filter: ReportPeriod;
  range: { start: string; end: string };
  kpis: {
    total_auctions: number;
    live_auctions: number;
    upcoming_auctions: number;
    completed_auctions: number;
    completed_without_bids: number;
    total_bids: number;
    deal_value: number;
    avg_bids_per_auction: number;
    total_users: number;
    new_users: number;
    channel_partners: number;
    active_bidders: number;
    participation_rate: number;
  };
  mode_breakdown: { reverse: number; forward: number };
  monthly_trends: Array<{ month: string; auctions: number; completed: number; bids: number; deal_value: number }>;
  top_auctions: Array<{
    id: number;
    title: string;
    status: string;
    auction_mode: string;
    currency: string;
    total_bids: number;
    bidders: number;
    best_amount: number | null;
  }>;
  top_bidders: Array<{ id: number; person_name: string; company_name: string; bids: number; auctions: number }>;
}

export interface AdminAuctionRow {
  id: number;
  auction_no: string;
  title: string;
  status: 'upcoming' | 'live' | 'completed' | string;
  auction_date: string;
  start_time: string;
  duration_minutes: number | null;
  currency: string;
  mode: 'reverse' | 'forward';
  type: string;
  step: number;
  creator_name: string | null;
  creator_company: string | null;
  participants: number;
  joined: number;
  total_bids: number;
  bidders: number;
  best_amount: number | null;
  winner_name: string | null;
  winner_company: string | null;
}

export interface AdminAuctionsReport {
  filter: ReportPeriod;
  summary: { total: number; live: number; upcoming: number; completed: number; total_bids: number; deal_value: number };
  auctions: AdminAuctionRow[];
}

export interface AdminUserRow {
  id: number;
  name: string | null;
  company: string | null;
  phone: string | null;
  email: string | null;
  role: 'user' | 'channel_partner';
  registered_at: string;
  status: 'active' | 'inactive' | 'blocked';
  auctions_created: number;
  bids_placed: number;
  auctions_bid_on: number;
  wins: number;
  won_value: number;
  last_bid: string | null;
}

export interface AdminUsersReport {
  filter: ReportPeriod;
  summary: { total: number; active: number; blocked: number; channel_partners: number; total_bids: number; total_wins: number };
  users: AdminUserRow[];
}

class AdminReportService {
  private getAuthHeaders(): HeadersInit {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    let adminToken: string | null = null;
    try {
      adminToken = sessionStorage.getItem('adminToken');
      if (!adminToken) {
        for (let i = 0; i < sessionStorage.length; i++) {
          const k = sessionStorage.key(i) || '';
          if (k.startsWith('admin_token')) {
            adminToken = sessionStorage.getItem(k);
            break;
          }
        }
      }
    } catch {
      /* ignore */
    }
    const authToken = adminToken || localStorage.getItem('authToken');
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
    return headers;
  }

  private async get<T>(path: string, params: Record<string, string | undefined>): Promise<T> {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v) qs.set(k, v);
    });
    const res = await fetch(`${ADMIN_REPORTS_BASE}/${path}?${qs.toString()}`, { headers: this.getAuthHeaders() });
    const text = await res.text();
    let json: any = {};
    try {
      json = text ? JSON.parse(text) : {};
    } catch {
      json = {};
    }
    if (!res.ok || json.success === false) {
      throw new Error(json.message || `Failed to load report (${res.status})`);
    }
    return json as T;
  }

  getDashboard(filter: ReportPeriod): Promise<AdminDashboardReport> {
    return this.get<AdminDashboardReport>('dashboard', { filter });
  }

  getAuctions(filter: ReportPeriod, status?: string, search?: string): Promise<AdminAuctionsReport> {
    return this.get<AdminAuctionsReport>('auctions', { filter, status, search });
  }

  getUsers(filter: ReportPeriod, role?: string, search?: string): Promise<AdminUsersReport> {
    return this.get<AdminUsersReport>('users', { filter, role, search });
  }
}

export default new AdminReportService();
